import React, { useRef } from "react";
import { Button, Col, Form, Row } from "react-bootstrap";
import ReCAPTCHA from "react-google-recaptcha";
import { Controller, useFormContext } from "react-hook-form";
import Select from "react-select";

import Loading from "@components/Loading";

import { selectAgeLevels } from "@features/global/globalSlice";

import { useAppSelector } from "@hooks/reduxHooks";
import useRecaptcha from "@hooks/useRecaptcha";

import { displayFormAlert } from "@lib/utils";

import useFormHandlers from "@Index/components/Contact/hooks/useFormHandlers";
import { IContactFormData } from "@Index/components/Contact/types/contact";

const ContactForm = (): React.ReactNode => {
    const ageLevels = useAppSelector(selectAgeLevels);
    const recaptcha = useRef<ReCAPTCHA>(null);
    const { onChangeRecaptcha } = useRecaptcha<IContactFormData>(recaptcha);

    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
    } = useFormContext<IContactFormData>();

    const { onSubmit, onError, submitting } = useFormHandlers(recaptcha);

    return (
        <Form onSubmit={handleSubmit(onSubmit, onError)} id="contactForm" aria-label="Contact form">
            <Row>
                <Col>
                    <h2>How can we help?</h2>
                </Col>
            </Row>
            <Row>
                <Col>
                    <h4>Please choose an option below.</h4>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Form.Select
                        {...register("contactType", { required: true })}
                        id="contactType"
                        className="mb-4"
                        defaultValue=""
                        aria-invalid={errors.contactType ? "true" : "false"}
                    >
                        <option disabled value="">
                            Select Option
                        </option>
                        <option value="existing">
                            I'm already part of the league and have a question
                        </option>
                        <option value="new">
                            I'm considering joining the league and have a
                            question
                        </option>
                    </Form.Select>
                    {errors.contactType &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.contactType.message as string,
                        })}
                </Col>
            </Row>
            <Row>
                <Form.Label>Name *</Form.Label>
                <Form.Group as={Col} controlId="firstName">
                    <Form.Control
                        type="text"
                        {...register("firstName", {
                            required: "First Name is required",
                        })}
                        aria-invalid={errors.firstName ? "true" : "false"}
                    />
                    <Form.Label>First</Form.Label>
                    {errors.firstName &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.firstName.message as string,
                        })}
                </Form.Group>
                <Form.Group as={Col} controlId="lastName">
                    <Form.Control
                        type="text"
                        {...register("lastName", {
                            required: "Last Name is required",
                        })}
                        aria-invalid={errors.lastName ? "true" : "false"}
                    />
                    <Form.Label>Last</Form.Label>
                    {errors.lastName &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.lastName.message as string,
                        })}
                </Form.Group>
            </Row>
            <Row>
                <Form.Label>Email *</Form.Label>
                <Form.Group as={Col} controlId="email">
                    <Form.Control
                        type="email"
                        {...register("email", {
                            required: "Email address  is required",
                        })}
                        aria-invalid={errors.email ? "true" : "false"}
                    />
                    <Form.Label>Enter Email</Form.Label>
                    {errors.email &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.email.message as string,
                        })}
                </Form.Group>
                <Form.Group as={Col} controlId="confirmEmail">
                    <Form.Control
                        type="email"
                        {...register("confirmEmail", {
                            required: "Please confirm your email address",
                        })}
                        aria-invalid={errors.confirmEmail ? "true" : "false"}
                    />
                    <Form.Label>Confirm Email</Form.Label>
                    {errors.confirmEmail &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.confirmEmail.message as string,
                        })}
                </Form.Group>
            </Row>
            <Row>
                <Col md={6}>
                    <Form.Group controlId="teamName">
                        <Form.Label>Team Name *</Form.Label>
                        <Form.Control
                            type="text"
                            {...register("teamName", {
                                required: "Team Name is required",
                            })}
                            aria-invalid={errors.teamName ? "true" : "false"}
                        />
                        {errors.teamName &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.teamName.message as string,
                            })}
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Col md={6}>
                    <Form.Group controlId="ageLevel">
                        <Form.Label>Age *</Form.Label>
                        <Controller
                            name="ageLevel"
                            control={control}
                            defaultValue={undefined}
                            render={({ field }) => (
                                <Select
                                    {...field}
                                    isSearchable={false}
                                    defaultValue={undefined}
                                    isClearable={true}
                                    required={false}
                                    options={ageLevels.map(a => {
                                        return {
                                            value: parseInt(a.id.toString()),
                                            label: a.ageLevel,
                                        };
                                    })}
                                    aria-invalid={
                                        errors.ageLevel ? "true" : "false"
                                    }
                                />
                            )}
                        />
                        {errors.ageLevel &&
                            displayFormAlert({
                                level: "danger",
                                text: errors.ageLevel.label?.message as string,
                            })}
                    </Form.Group>
                </Col>
            </Row>
            <Row>
                <Form.Label>Comment</Form.Label>
                <Form.Group as={Col} controlId="comment">
                    <Form.Control
                        as="textarea"
                        rows={10}
                        cols={50}
                        {...register("comment")}
                    />
                </Form.Group>
            </Row>
            <Row>
                <Col xs={3}>
                    <Form.Label>CAPTCHA</Form.Label>
                    <ReCAPTCHA
                        ref={recaptcha}
                        sitekey={process.env.RECAPTCHA_SITE_KEY as string}
                        onChange={onChangeRecaptcha}
                    />
                    {errors.recaptcha &&
                        displayFormAlert({
                            level: "danger",
                            text: errors.recaptcha.message as string,
                        })}
                </Col>
            </Row>
            <Row>
                <Col className="d-flex align-items-baseline">
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={submitting === "pending"}
                    >
                        Submit
                    </Button>
                    {submitting === "pending" && (
                        <Loading className="mx-3 w-auto" />
                    )}
                </Col>
            </Row>
        </Form>
    );
};

export default ContactForm;
